/**
 * 系统统计组件 - 显示用户和系统统计信息
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl, API_ENDPOINTS } from '../../config/api';

interface UserStats {
  total: number;
  verified: number;
  unverified: number;
  admins: number;
  activeUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
}

const SystemStats: React.FC = () => {
  const { token } = useAuth();
  
  // 处理API响应的通用函数
  const handleApiResponse = async (response: Response) => {
    if (response.status === 401) {
      const data = await response.json();
      const errorCode = data.error?.code;
      
      if (errorCode === 'ACCOUNT_DISABLED' || 
          errorCode === 'INVALID_TOKEN' || 
          errorCode === 'USER_NOT_FOUND') {
        // 清除本地存储的认证信息
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        
        // 重定向到登录页面
        window.location.href = '/auth/login';
        
        // 显示错误消息
        if (errorCode === 'ACCOUNT_DISABLED') {
          alert('您的账号已被禁用，请联系管理员');
        } else {
          alert('登录已过期，请重新登录');
        }
        
        throw new Error('认证失败');
      }
    }
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error?.message || '请求失败');
    }
    
    return await response.json();
  };
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN.USER_STATS), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await handleApiResponse(response);
      if (data.success) {
        setStats(data.data);
        setError(null);
      } else {
        throw new Error(data.error?.message || '获取统计信息失败');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取统计信息失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-lg p-4">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-8">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">加载失败</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchStats}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const statCards = [
    {
      title: '总用户数',
      value: stats.total,
      icon: '👥',
      color: 'blue',
      description: '系统注册用户总数',
    },
    {
      title: '已验证用户',
      value: stats.verified,
      icon: '✅',
      color: 'green',
      description: '已完成邮箱验证的用户',
      percentage: stats.total > 0 ? Math.round((stats.verified / stats.total) * 100) : 0,
    },
    {
      title: '未验证用户',
      value: stats.unverified,
      icon: '⏳',
      color: 'yellow',
      description: '尚未完成邮箱验证的用户',
      percentage: stats.total > 0 ? Math.round((stats.unverified / stats.total) * 100) : 0,
    },
    {
      title: '管理员',
      value: stats.admins,
      icon: '👑',
      color: 'purple',
      description: '系统管理员数量',
    },
    {
      title: '今日新增',
      value: stats.newUsersToday,
      icon: '📈',
      color: 'indigo',
      description: '今天新注册的用户',
    },
    {
      title: '本周新增',
      value: stats.newUsersThisWeek,
      icon: '📊',
      color: 'teal',
      description: '本周新注册的用户',
    },
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      green: 'bg-green-50 text-green-700 border-green-200',
      yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      purple: 'bg-purple-50 text-purple-700 border-purple-200',
      indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      teal: 'bg-teal-50 text-teal-700 border-teal-200',
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">系统概览</h2>
            <p className="text-gray-600 mt-1">实时系统统计和用户数据</p>
          </div>
          <button
            onClick={fetchStats}
            className="flex items-center px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            <span className="mr-2">🔄</span>
            刷新数据
          </button>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <div
            key={index}
            className={`bg-white rounded-lg shadow border-l-4 ${getColorClasses(card.color)} p-6 transition-all hover:shadow-lg`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">{card.title}</p>
                <p className="text-3xl font-bold text-gray-900">{card.value.toLocaleString()}</p>
                {card.percentage !== undefined && (
                  <p className="text-sm text-gray-500 mt-1">{card.percentage}% 占比</p>
                )}
              </div>
              <div className="text-4xl opacity-80">{card.icon}</div>
            </div>
            <p className="text-sm text-gray-500 mt-3">{card.description}</p>
          </div>
        ))}
      </div>

      {/* 趋势图表区域（占位符） */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">用户增长趋势</h3>
        <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <div className="text-4xl text-gray-400 mb-2">📈</div>
            <p className="text-gray-500">图表功能开发中...</p>
            <p className="text-sm text-gray-400 mt-1">将显示用户注册和活跃度趋势</p>
          </div>
        </div>
      </div>

      {/* 快速操作 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="flex items-center justify-center px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
            <span className="mr-2">👥</span>
            查看用户列表
          </button>
          <button className="flex items-center justify-center px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors">
            <span className="mr-2">🎫</span>
            管理邀请码
          </button>
          <button className="flex items-center justify-center px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors">
            <span className="mr-2">📧</span>
            邮件统计
          </button>
          <button className="flex items-center justify-center px-4 py-3 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
            <span className="mr-2">⚙️</span>
            系统设置
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemStats; 